import React from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import tinycolor from "tinycolor2";
import { useTheme } from "./ThemeProvider";

export default function ThemeModal({ isOpen, onClose }) {
    const { themeId, setThemeId, themes, currentTheme } = useTheme();
    if (!isOpen || typeof document === "undefined") return null;

    const themeOptions = themes.map((theme) => ({
        value: theme.id,
        label: theme.name,
    }));

    const selectedTheme =
        themeOptions.find((option) => option.value === themeId) || null;

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 48,
            borderRadius: 14,
            borderColor: state.isFocused ? currentTheme.color : "#dbe2ea",
            boxShadow: state.isFocused ? `0 0 0 3px ${tinycolor(currentTheme.color).setAlpha(0.16).toRgbString()}` : "none",
            backgroundColor: "#ffffff",
            "&:hover": {
                borderColor: currentTheme.color,
            },
        }),
        valueContainer: (base) => ({
            ...base,
            padding: "0 14px",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#94a3b8",
        }),
        singleValue: (base) => ({
            ...base,
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 600,
        }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: state.isFocused ? currentTheme.color : "#64748b",
            "&:hover": {
                color: currentTheme.color,
            },
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 10020,
        }),
        menu: (base) => ({
            ...base,
            zIndex: 10020,
            overflow: "hidden",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.14)",
        }),
        option: (base, state) => ({
            ...base,
            fontSize: 14,
            color: state.isSelected ? "#ffffff" : "#0f172a",
            backgroundColor: state.isSelected
                ? currentTheme.color
                : state.isFocused
                    ? tinycolor(currentTheme.color).setAlpha(0.08).toRgbString()
                    : "#ffffff",
            "&:active": {
                backgroundColor: state.isSelected
                    ? currentTheme.color
                    : tinycolor(currentTheme.color).setAlpha(0.14).toRgbString(),
            },
        }),
    };

    return createPortal((
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-auto relative z-[10000]">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>Color Theme</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl font-bold focus:outline-none transition-colors"
                    >
                        ×
                    </button>
                </div>
                <div className="space-y-4">
                    <Select
                        inputId="theme-select"
                        options={themeOptions}
                        value={selectedTheme}
                        onChange={(option) => option?.value && setThemeId(option.value)}
                        isSearchable={false}
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                    />
                    <div className="flex justify-center mt-6">
                        <div className="flex flex-col items-center">
                            <div
                                className="w-16 h-16 rounded-full border-4 border-gray-200 shadow-lg transition-colors"
                                style={{ backgroundColor: currentTheme.color }}
                            ></div>
                            <span className="text-sm mt-3 font-medium" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                                Selected Color
                            </span>
                        </div>
                    </div>
                    <div className="flex justify-center mt-8">
                        <button
                            onClick={onClose}
                            className="px-8 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}
